import React, { useState } from 'react';
import { CaretDown, CaretUp, PencilSimple, Sparkle, Archive, ArrowUUpLeft } from '@phosphor-icons/react';
import { OfferStatusPill } from './OfferStatusPill';
import { OfferListingPreview } from './OfferListingPreview';
import { OfferPhotoGrid } from './OfferPhotoGrid';
import { ReceivedOfferCard } from './ReceivedOfferCard';
import { Button } from '../ui/Button';
import { ActionMenu } from '../ui/ActionMenu';
import { cn } from '../../lib/utils';
import type { OfferStatus } from '../../lib/utils';
export interface OfferData {
  id: string;
  status: OfferStatus;
  counterpartyName: string;
  counterpartyAvatar?: string;
  sentDate: string;
  message: string;
  listingPreview?: {
    image?: string;
    title: string;
    priceLabel: string;
    locationLabel: string;
    url: string;
  };
  photos?: string[];
  contactEmail?: string;
  contactPhone?: string;
  acceptNote?: string;
  rejectNote?: string;
  matchScore?: string; // e.g. '5/7'
  matchedFeatures?: string[];
  missingFeatures?: string[];
}
interface OfferCardProps {
  offer: OfferData;
  viewerRole: 'sender' | 'receiver';
  onAccept?: () => void;
  onReject?: () => void;
  onPostpone?: () => void;
  onStatusChange?: (newStatus: OfferStatus) => void;
  onEdit?: () => void;
  onUpdateAfterRejection?: () => void;
  onViewTenantAnunt?: () => void;
  tenantName?: string;
  onMarkSucces?: () => void;
  onRetract?: (offerId: string) => void;
  onViewSupplyProfile?: () => void;
}
export function OfferCard({
  offer,
  viewerRole,
  onAccept,
  onReject,
  onPostpone,
  onStatusChange,
  onEdit,
  onUpdateAfterRejection,
  onViewTenantAnunt,
  tenantName,
  onMarkSucces,
  onRetract,
  onViewSupplyProfile
}: OfferCardProps) {
  const [expanded, setExpanded] = useState(false);
  // Delegate to ReceivedOfferCard for receiver role — different UX (always-expanded, full layout)
  if (viewerRole === 'receiver') {
    return <ReceivedOfferCard offer={offer} tenantName={tenantName} onAccept={onAccept} onReject={onReject} onPostpone={onPostpone} onStatusChange={onStatusChange} onMarkSucces={onMarkSucces} onViewSupplyProfile={onViewSupplyProfile} data-id="element-567" />;
  }
  const isSender = viewerRole === 'sender';
  const handleExpandToggle = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setExpanded(!expanded);
  };
  // Handle click on the card body (not on interactive elements)
  const handleCardClick = (e: React.MouseEvent) => {
    // Only toggle if clicking the card itself, not buttons/links inside
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    handleExpandToggle();
  };
  const canRetract = isSender && ['new', 'saved', 'postponed'].includes(offer.status);
  return <div className={cn('rounded-2xl bg-dark-2 border transition-colors overflow-hidden', expanded ? 'border-light-4' : 'border-dark-4 hover:border-light-4 cursor-pointer')} onClick={!expanded ? handleCardClick : undefined} data-id="element-568">
      <div className="p-5 sm:p-6" data-id="element-569">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4" data-id="element-570">
          <div className="flex items-center gap-3" data-id="element-571">
            {offer.counterpartyAvatar ? <img src={offer.counterpartyAvatar} alt={offer.counterpartyName} className="w-10 h-10 rounded-full object-cover" data-id="element-572" /> : <div className="w-10 h-10 rounded-full bg-dark-3 flex items-center justify-center text-light-2 font-medium" data-id="element-573">
                {offer.counterpartyName.charAt(0)}
              </div>}
            <div data-id="element-574">
              <p className="font-semibold text-light-1" data-id="element-575">
                {offer.counterpartyName}
              </p>
              <p className="text-xs text-light-4" data-id="element-576">{offer.sentDate}</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-577">
            <OfferStatusPill status={offer.status} variant={viewerRole} data-id="element-578" />
            {canRetract && onRetract && <div className="-mr-2" data-id="element-579">
                <ActionMenu triggerAriaLabel="Acțiuni ofertă" items={[{
              label: 'Retrage oferta',
              icon: <ArrowUUpLeft size={18} weight="regular" data-id="element-581" />,
              onClick: () => onRetract(offer.id),
              destructive: true
            }]} data-id="element-580" />
              </div>}
          </div>
        </div>

        {/* Message Bubble */}
        <div className="bg-dark-3 p-3 rounded-xl border border-dark-4" data-id="element-582">
          <p className={cn('text-sm text-light-2 italic leading-relaxed', !expanded && 'line-clamp-2')} data-id="element-583">
            "{offer.message}"
          </p>
        </div>

        {/* Expanded Content: Listing Preview */}
        {expanded && offer.listingPreview && <div className="mt-4" data-id="element-584">
            <OfferListingPreview {...offer.listingPreview} data-id="element-585" />
          </div>}

        {/* Expanded Content: Photos */}
        {expanded && offer.photos && offer.photos.length > 0 && <div className="mt-4" data-id="element-586">
            <OfferPhotoGrid photos={offer.photos} data-id="element-587" />
          </div>}

        {/* Expanded Content: Success Banner (Sender view of 'saved') */}
        {expanded && offer.status === 'saved' && <div className="mt-6 bg-lime-5 border border-lime-4 rounded-xl p-4" data-id="element-588">
            <p className="text-sm text-light-1 font-medium mb-2" data-id="element-589">
              {offer.counterpartyName} a salvat oferta ta și este interesat.
            </p>
            <p className="text-xs text-light-3" data-id="element-590">
              Chiriașul are datele tale de contact și te va contacta dacă decide
              să meargă mai departe.
            </p>
          </div>}

        {/* Expanded Content: Succes Banner (Sender view of 'succes') */}
        {expanded && offer.status === 'succes' && <div className="mt-6 bg-lime-5 border border-lime-4 rounded-xl p-4 flex items-start gap-3" data-id="element-591">
            <div className="w-10 h-10 rounded-2xl bg-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-592">
              <Sparkle size={20} weight="fill" className="text-lime-1" data-id="element-593" />
            </div>
            <div className="min-w-0" data-id="element-594">
              <p className="text-sm font-semibold text-light-1" data-id="element-595">
                Felicitări — {offer.counterpartyName} s-a mutat. 🎉
              </p>
              <p className="text-xs text-light-3 mt-1 leading-relaxed" data-id="element-596">
                Asta îți construiește reputația pe Izio. Peste un an, vei putea
                cere un review de la chiriaș direct din profil.
              </p>
            </div>
          </div>}

        {/* Expanded Content: Archived Banner (Sender view of 'archived') */}
        {expanded && offer.status === 'archived' && <div className="mt-6 bg-dark-3 border border-dark-4 rounded-xl p-4 flex items-start gap-3" data-id="element-597">
            <div className="w-10 h-10 rounded-2xl bg-dark-4 inline-flex items-center justify-center shrink-0" data-id="element-598">
              <Archive size={18} weight="duotone" className="text-light-3" data-id="element-599" />
            </div>
            <div className="min-w-0" data-id="element-600">
              <p className="text-sm font-medium text-light-1" data-id="element-601">
                Această ofertă a fost arhivată.
              </p>
              <p className="text-xs text-light-3 mt-1 leading-relaxed" data-id="element-602">
                {offer.counterpartyName} a închiriat în altă parte. Nu e
                personal — căutarea s-a încheiat înainte ca tu să poți răspunde.
              </p>
            </div>
          </div>}

        {/* Expanded Content: Reject Banner & Update CTA */}
        {expanded && offer.status === 'rejected' && offer.rejectNote && <div className="mt-6 bg-red-4 border border-red-2 rounded-xl p-4" data-id="element-603">
            <div className="pl-3 border-l-2 border-red-2 mb-4" data-id="element-604">
              <p className="text-sm text-light-2 italic" data-id="element-605">
                "{offer.rejectNote}"
              </p>
            </div>

            {isSender && onUpdateAfterRejection && <Button onClick={onUpdateAfterRejection} className="bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm h-9 px-4" data-id="element-606">
                Trimite ofertă actualizată
              </Button>}
          </div>}

        {/* Footer Row */}
        <div className={cn('flex items-center justify-between gap-4', expanded ? 'mt-6 pt-4 border-t border-dark-4' : 'mt-4')} data-id="element-607">
          <div className="flex items-center gap-4" data-id="element-608">
            {onViewTenantAnunt && <button type="button" onClick={e => {
            e.stopPropagation();
            onViewTenantAnunt();
          }} className="text-sm font-medium text-light-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-609">
                Vezi anunțul →
              </button>}

            {expanded && isSender && offer.status === 'new' && onEdit && <button onClick={e => {
            e.stopPropagation();
            onEdit();
          }} className="text-sm font-medium text-light-3 hover:text-light-1 transition-colors inline-flex items-center gap-1.5" data-id="element-610">
                <PencilSimple size={16} data-id="element-611" />
                Editează oferta
              </button>}
          </div>

          <button onClick={handleExpandToggle} className="text-xs font-medium text-light-3 hover:text-light-1 transition-colors inline-flex items-center gap-1 ml-auto" data-id="element-612">
            {expanded ? <>
                Mai puțin <CaretUp size={14} data-id="element-613" />
              </> : <>
                Vezi mai mult <CaretDown size={14} data-id="element-614" />
              </>}
          </button>
        </div>
      </div>
    </div>;
}